import { reflexFor } from '../coaching/explain';
import { puzzleThemeLabel } from '../puzzles/themes';
import type { CoachingReport, PuzzleAttempt, StoredGame, Theme } from '../../types';

/**
 * Diagnostic multi-parties : mesure de progression construite entièrement
 * sur des données déjà vérifiées mécaniquement (stats de balayage, thèmes
 * détectés, tentatives de puzzles enregistrées) — aucun score de niveau
 * inventé. Fonction pure, sans accès au stockage : à appeler avec les
 * données déjà chargées par useLibrary.
 */

const RECENT_WINDOW = 10;

export interface GameMistakeStat {
  gameId: string;
  label: string;
  playerMoves: number;
  inaccuracies: number;
  mistakes: number;
  blunders: number;
  /** (mistakes + blunders) / playerMoves — mesure la plus parlante pour comparer des parties de longueurs différentes. */
  mistakeRate: number;
}

export interface DiagnosticTrend {
  direction: 'improving' | 'worsening' | 'stable';
  /** Différence de taux de gaffes (fenêtre récente − fenêtre précédente). Négatif = amélioration. */
  deltaRate: number;
}

export interface PrimaryWeakness {
  theme: Theme;
  count: number;
  share: number; // part de ce thème parmi tous les thèmes détectés dans la fenêtre récente
  reflex: string;
}

export interface PuzzleThemeStat {
  theme: string;
  label: string;
  total: number;
  solved: number; // résolus du premier coup
}

export interface EloPoint {
  date: string; // ISO
  rating: number;
  timeClass: string;
}

export interface Diagnostic {
  recentGames: GameMistakeStat[]; // jusqu'à 10, ordre chronologique (plus ancienne en premier)
  trend: DiagnosticTrend | null; // null s'il n'y a pas assez de recul (< 4 parties récentes, ou aucune fenêtre précédente)
  primaryWeakness: PrimaryWeakness | null;
  puzzleThemeStats: PuzzleThemeStat[];
  eloSeries: EloPoint[];
}

export interface ComputeDiagnosticInput {
  games: StoredGame[];
  reports: Record<string, CoachingReport>;
  puzzleAttempts: PuzzleAttempt[];
}

function gameTimestamp(stored: StoredGame): number {
  if (stored.chessCom?.endTime) return stored.chessCom.endTime * 1000;
  const headerDate = stored.game.headers.Date;
  if (headerDate) {
    const parsed = Date.parse(headerDate.replace(/\./g, '-'));
    if (!Number.isNaN(parsed)) return parsed;
  }
  const imported = Date.parse(stored.importedAt);
  return Number.isNaN(imported) ? 0 : imported;
}

function formatGameLabel(stored: StoredGame): string {
  const opponent = stored.chessCom?.opponent;
  const date = new Date(gameTimestamp(stored));
  const dateLabel = Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  return opponent ? `${dateLabel} vs ${opponent}` : dateLabel || stored.game.id;
}

interface GameWithReport {
  game: StoredGame;
  report: CoachingReport;
}

function windowMistakeRate(window: GameWithReport[]): number {
  let totalMoves = 0;
  let totalMistakes = 0;
  for (const { report } of window) {
    const stats = report.stats!;
    totalMoves += stats.playerMoves;
    totalMistakes += stats.mistakes + stats.blunders;
  }
  return totalMoves > 0 ? totalMistakes / totalMoves : 0;
}

function computeTrend(recent: GameWithReport[], previous: GameWithReport[]): DiagnosticTrend | null {
  if (recent.length < 4 || previous.length === 0) return null;
  const recentRate = windowMistakeRate(recent);
  const previousRate = windowMistakeRate(previous);
  const deltaRate = recentRate - previousRate;
  const direction = deltaRate < -0.01 ? 'improving' : deltaRate > 0.01 ? 'worsening' : 'stable';
  return { direction, deltaRate };
}

function computePrimaryWeakness(reports: CoachingReport[]): PrimaryWeakness | null {
  const counts = new Map<Theme, number>();
  let total = 0;
  for (const report of reports) {
    for (const moment of report.momentsForExercises) {
      for (const theme of moment.themes) {
        counts.set(theme, (counts.get(theme) ?? 0) + 1);
        total += 1;
      }
    }
  }
  if (counts.size === 0) return null;
  const [theme, count] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  return { theme, count, share: total > 0 ? count / total : 0, reflex: reflexFor(theme) };
}

function computePuzzleThemeStats(attempts: PuzzleAttempt[]): PuzzleThemeStat[] {
  const byTheme = new Map<string, { total: number; solved: number }>();
  for (const attempt of attempts) {
    for (const theme of attempt.themes) {
      if (puzzleThemeLabel(theme) === theme) continue; // ignore les tags Lichess génériques (pas un de nos 12 thèmes)
      const stat = byTheme.get(theme) ?? { total: 0, solved: 0 };
      stat.total += 1;
      if (attempt.solvedFirstTry) stat.solved += 1;
      byTheme.set(theme, stat);
    }
  }
  return [...byTheme.entries()]
    .map(([theme, stat]) => ({ theme, label: puzzleThemeLabel(theme), ...stat }))
    .sort((a, b) => b.total - a.total);
}

function computeEloSeries(games: StoredGame[]): EloPoint[] {
  return games
    .filter((g): g is StoredGame & { chessCom: NonNullable<StoredGame['chessCom']> } => g.chessCom?.playerRating != null)
    .map((g) => ({
      date: new Date(g.chessCom.endTime * 1000).toISOString(),
      rating: g.chessCom.playerRating as number,
      timeClass: g.chessCom.timeClass,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function computeDiagnostic({ games, reports, puzzleAttempts }: ComputeDiagnosticInput): Diagnostic {
  const withStats: GameWithReport[] = games
    .map((game) => ({ game, report: reports[game.game.id] }))
    .filter((x): x is GameWithReport => Boolean(x.report?.stats))
    .sort((a, b) => gameTimestamp(a.game) - gameTimestamp(b.game));

  const recentWindow = withStats.slice(-RECENT_WINDOW);
  const previousWindow = withStats.slice(-RECENT_WINDOW * 2, -RECENT_WINDOW);

  const recentGames: GameMistakeStat[] = recentWindow.map(({ game, report }) => {
    const stats = report.stats!;
    const mistakeRate = stats.playerMoves > 0 ? (stats.mistakes + stats.blunders) / stats.playerMoves : 0;
    return {
      gameId: game.game.id,
      label: formatGameLabel(game),
      playerMoves: stats.playerMoves,
      inaccuracies: stats.inaccuracies,
      mistakes: stats.mistakes,
      blunders: stats.blunders,
      mistakeRate,
    };
  });

  return {
    recentGames,
    trend: computeTrend(recentWindow, previousWindow),
    primaryWeakness: computePrimaryWeakness(recentWindow.map((w) => w.report)),
    puzzleThemeStats: computePuzzleThemeStats(puzzleAttempts),
    eloSeries: computeEloSeries(games),
  };
}
